import { signatureApi } from '@/api/weChat';
import { PluginKey, LogModule } from '@/data/config';
import { WeChat } from '@/interface';

import loadJS from '@/modules/loadjs';
import { isWeChat, wechatVersion } from '@/modules/detector';
import { loggerInfo, loggerWarn } from '@/modules/logger';
import { getPluginUrl } from '@/modules/config';

// 验证是否在微信浏览器
export function checkWeChat(text = '非微信浏览器打开') {
  if (!isWeChat()) {
    loggerWarn(text, LogModule.WeChat);
    return {
      valid: false,
      message: text,
    };
  }

  return {
    valid: true,
  };
}

// ------------ SDK 相关方法 -------------------
/**
 * 加载weChat SDK
 * @param wechatUrl
 * @returns {wx} window.wx
 * @returns {miniprogram} window.wx.miniProgram
 */
export async function weChatLoadSDK(wechatUrl?: string) {
  return await loadJS(wechatUrl ?? getPluginUrl().WECHAT, PluginKey.WECHAT, 'wechat').then(() => {
    console.info('微信浏览器版本号', wechatVersion());
    return {
      wx: window.wx,
      miniProgram: window.wx?.miniProgram,
      weixinJSBridge: window?.WeixinJSBridge,
    };
  });
}

interface IWXConfigParams {
  debug: boolean;
  appId: string; // 必填，公众号的唯一标识
  timestamp: string; // 必填，生成签名的时间戳
  nonceStr: string; // 必填，生成签名的随机串
  signature: string; // 必填，签名，见附录1
  jsApiList: WeChat.jsApiListType[];
}

/**
 * 微信SDK配置方法
 */
export const weChatSDKConfig = (params: IWXConfigParams): void => {
  if (window?.wx && window?.wx.config) {
    window.wx.config(
      Object.assign(
        {
          debug: false,
        },
        {
          ...params,
          jsApiList: params.jsApiList.slice(),
        }
      )
    );
  }
};

/**
 * 微信 加载SDK config信息验证后会执行ready方法
 * @returns {Promise<void>}
 */
export const weChatSDKReady = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window?.wx && window?.wx.ready && window?.wx.error) {
      window.wx.ready(function () {
        resolve();
      });

      window.wx.error(function (error: unknown) {
        reject(error);
        // config信息验证失败会执行error函数，如签名过期导致验证失败，具体错误信息可以打开config的debug模式查看，也可以在返回的res参数中查看，对于SPA可以在这里更新签名。
      });
    }
  });
};

/**
 * 微信签名
 */
export async function weChatSDKSign(options: {
  wechatUrl?: string;
  reqUrl?: string;
  url?: string;
  jsApiList?: WeChat.jsApiListType[];
  debug?: boolean;
}) {
  // 动态加载wechat
  await weChatLoadSDK(options.wechatUrl);

  // 获取签名配置
  const signatureRes = await signatureApi(options?.reqUrl, options?.url);

  loggerInfo(['簽名返回值', signatureRes], LogModule.WeChat);

  // 添加微信配置
  weChatSDKConfig({
    ...signatureRes.data,
    debug: options.debug ?? false,
    jsApiList: options?.jsApiList ?? [],
  });

  // 等待wechat ready回调
  await weChatSDKReady();

  loggerInfo('wechat ready 回调', LogModule.WeChat);
}

// --------------- end ------------------

const enum ListenerType {
  WeChatJSBridgeReady = 'WeixinJSBridgeReady',
}

function addEvent(type: ListenerType) {
  return new Promise((resolve, reject) => {
    try {
      document.addEventListener(type, resolve, false);
    } catch (e) {
      reject();
    }
  });
}

const weChatJSBridgeReady = () => addEvent(ListenerType.WeChatJSBridgeReady);

/**
 * 获取 微信 WeixinJSBridge
 */
export async function getWeChatJSBridge() {
  // 校验是否在微信浏览器
  const { valid } = checkWeChat();
  if (!valid) return;

  if (window.WeixinJSBridge && window.WeixinJSBridge.invoke) {
    loggerInfo('WeixinJSBridge 获取成功', LogModule.WeChat);
    return window.WeixinJSBridge;
  }
  await weChatJSBridgeReady();
  loggerInfo('WeixinJSBridge 获取成功', LogModule.WeChat);

  return window.WeixinJSBridge;
}
